// src/components/contentGeneration/PricingConfirmationStep.js
import React, { useState } from 'react';
import { Check, Zap, Sparkles, Lock, Crown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { createPaymentIntent, verifyAndCreateJob } from '../../services/contentPayment';
import { contentGenerationAPI } from '../../services/api';
import { isAdmin } from '../../services/admin';
import PaymentModal from './PaymentModal';
import toast from 'react-hot-toast';

const PricingConfirmationStep = ({
  selectedSources,
  outline,
  settings,
  selectedTier,
  setSelectedTier,
  onNext,
  onBack,
  setJobId
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState('');

  const estimatedPages = Math.ceil(settings.target_words / 250);

  const tiers = [
    {
      id: 'standard',
      name: 'Standard',
      icon: Zap,
      pricePerPage: 1.49,
      model: 'Claude Sonnet',
      features: [
        'High-quality AI writing',
        'Natural source integration',
        'Multiple writing approaches & tones',
        'Edit & revise before download',
        'Export as Word or text'
      ],
      color: 'primary'
    },
    {
      id: 'pro',
      name: 'Professional',
      icon: Sparkles,
      pricePerPage: 2.49,
      model: 'Claude Opus',
      features: [
        'Most capable AI writing model',
        'Deeper research synthesis & nuance',
        'Richer cross-source connections',
        'More sophisticated prose architecture',
        'Priority support',
        'Export as Word or text'
      ],
      color: 'accent',
      badge: 'RECOMMENDED'
    }
  ];

  const selectedTierData = tiers.find(t => t.id === selectedTier);
  const totalCost = (estimatedPages * selectedTierData.pricePerPage).toFixed(2);

  const handleConfirmAndPay = async () => {
    try {
      setLoading(true);

      // Admin bypass - create job directly without payment
      if (isAdmin(currentUser)) {
        toast.success('Admin access: Bypassing payment');

        const response = await contentGenerationAPI.createJob(
          currentUser.uid,
          selectedSources.map(s => s.id),
          outline,
          settings,
          selectedTier
        );

        if (!response || !response.job_id) {
          throw new Error('Invalid response from server - no job ID received');
        }

        setJobId(response.job_id);
        toast.success('Generation started! (Admin - No charge)');
        onNext();
        setLoading(false);
        return;
      }

      // Regular user flow - Create Stripe Payment Intent
      const jobMetadata = {
        source_ids: selectedSources.map(s => s.id),
        outline: outline,
        settings: settings
      };

      const paymentData = await createPaymentIntent(
        currentUser.uid,
        selectedTier,
        estimatedPages,
        jobMetadata
      );

      // Store client secret for Stripe Elements
      setPaymentClientSecret(paymentData.client_secret);

      // Show payment modal
      setShowPaymentModal(true);
      setLoading(false);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      toast.error(error.message || 'Failed to initialize payment');
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      setLoading(true);

      // Verify payment and create generation job
      const response = await verifyAndCreateJob(
        paymentIntentId,
        currentUser.uid,
        selectedSources.map(s => s.id),
        outline,
        settings,
        selectedTier
      );

      if (!response || !response.job_id) {
        throw new Error('Invalid response from server - no job ID received');
      }

      setJobId(response.job_id);
      setShowPaymentModal(false);
      toast.success('Payment successful! Starting generation...');
      onNext();
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error(error.message || 'Failed to create generation job');
      setLoading(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    console.error('Payment error:', errorMessage);
    toast.error(errorMessage || 'Payment failed');
    setLoading(false);
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setLoading(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-secondary-900 mb-4">
        Choose Your Quality Tier
      </h2>
      <p className="text-gray-600 mb-6">
        Select the AI model quality and features that best fit your needs.
      </p>

      {/* Pricing Tiers */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {tiers.map(tier => {
          const Icon = tier.icon;
          const isSelected = selectedTier === tier.id;
          const tierCost = (estimatedPages * tier.pricePerPage).toFixed(2);

          return (
            <div
              key={tier.id}
              onClick={() => setSelectedTier(tier.id)}
              className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                isSelected
                  ? tier.color === 'primary'
                    ? 'border-primary bg-primary-50 shadow-lg'
                    : 'border-primary bg-primary/5 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {tier.badge && (
                <span className="absolute -top-3 right-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                  {tier.badge}
                </span>
              )}

              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    tier.color === 'primary' ? 'bg-primary-100' : 'bg-primary/10'
                  }`}>
                    <Icon className={`w-6 h-6 ${tier.color === 'primary' ? 'text-primary' : 'text-primary'}`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-secondary-900">{tier.name}</h3>
                    <p className="text-xs text-gray-600">{tier.model}</p>
                  </div>
                </div>

                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected
                    ? tier.color === 'primary'
                      ? 'bg-primary border-primary'
                      : 'bg-primary border-primary'
                    : 'border-gray-300'
                }`}>
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline space-x-1">
                  <span className="text-3xl font-bold text-secondary-900">${tier.pricePerPage}</span>
                  <span className="text-gray-600">/page</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Your document: <span className="font-semibold">${tierCost}</span>
                </p>
              </div>

              <ul className="space-y-1.5">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                    <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${tier.color === 'primary' ? 'text-primary' : 'text-accent'}`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Order Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
        <h3 className="font-bold text-lg text-secondary-900 mb-4">Order Summary</h3>

        <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Sources Selected</span>
            <span className="font-medium text-secondary-900">{selectedSources.length} sources</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Document Type</span>
            <span className="font-medium text-secondary-900 capitalize">
              {settings.document_type.replace('_', ' ')}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Target Length</span>
            <span className="font-medium text-secondary-900">
              {settings.target_words} words ({estimatedPages} pages)
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Approach</span>
            <span className="font-medium text-secondary-900 capitalize">{settings.approach}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Quality Tier</span>
            <span className="font-medium text-secondary-900">{selectedTierData.name}</span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span className="font-bold text-lg text-secondary-900">Total</span>
          <div className="text-right">
            <span className="font-bold text-2xl text-secondary-900">${totalCost}</span>
            <p className="text-xs text-gray-600">Pay after generation</p>
          </div>
        </div>
      </div>

      {/* Admin Notice or Important Note */}
      {isAdmin(currentUser) ? (
        <div className="bg-primary/5 border-2 border-primary/40 rounded-lg p-4 mb-6 flex items-start space-x-3">
          <Crown className="w-6 h-6 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-primary-900 mb-1">Admin Access - Free Generation</p>
            <p className="text-xs text-primary-700">
              You have admin privileges. This generation will be <strong>FREE</strong> and bypass payment processing.
              The system will create the job directly for testing purposes.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
          <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-primary-900 mb-1">Secure Payment Before Generation</p>
            <p className="text-xs text-primary-700">
              You'll pay <strong>${totalCost}</strong> before generation starts.
              If generation fails for any reason, you'll receive an automatic 100% refund.
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <button
          onClick={onBack}
          disabled={loading}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
        >
          ← Back
        </button>
        <div className="flex items-center space-x-3">
          <p className="text-sm text-gray-600">Step 4 of 6</p>
          <button
            onClick={handleConfirmAndPay}
            disabled={loading}
            className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : isAdmin(currentUser)
                ? 'bg-primary text-white hover:bg-primary-700 shadow-lg'
                : 'bg-primary text-white hover:bg-primary-700 shadow-lg'
            }`}
          >
            {loading ? 'Processing...' : isAdmin(currentUser) ? 'Generate (Admin - Free)' : `Pay & Generate - $${totalCost}`}
          </button>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentCancel}
        clientSecret={paymentClientSecret}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        totalCost={totalCost}
        tier={selectedTier}
      />
    </div>
  );
};

export default PricingConfirmationStep;
